import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  useEffect(() => {
    const messagesQuery = query(
      collection(db, 'contact_messages'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const markAsRead = async (messageId) => {
    try {
      await updateDoc(doc(db, 'contact_messages', messageId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markAsUnread = async (messageId) => {
    try {
      await updateDoc(doc(db, 'contact_messages', messageId), {
        read: false
      });
    } catch (error) {
      console.error('Error marking message as unread:', error);
    }
  };

  const filteredMessages = messages.filter(message => {
    if (filter === 'unread') return !message.read;
    if (filter === 'read') return message.read;
    return true;
  });

  const unreadCount = messages.filter(msg => !msg.read).length;

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading messages...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '28px', 
            fontWeight: '700',
            color: '#222'
          }}>
            Contact Messages
          </h2>
          <div style={{ 
            fontSize: '14px', 
            color: '#666',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <span>Total: {messages.length}</span>
            {unreadCount > 0 && (
              <span style={{
                background: '#e74c3c',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {unreadCount} unread
              </span>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              background: filter === 'all' ? '#222' : '#fff',
              color: filter === 'all' ? '#fff' : '#222',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            All ({messages.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              background: filter === 'unread' ? '#222' : '#fff',
              color: filter === 'unread' ? '#fff' : '#222',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter('read')}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              background: filter === 'read' ? '#222' : '#fff',
              color: filter === 'read' ? '#fff' : '#222',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Read ({messages.length - unreadCount})
          </button>
        </div>
      </div>

      {filteredMessages.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#f8f8f8',
          border: '1px solid #eee'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📫</div>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '8px' }}>
            {filter === 'unread' ? 'No unread messages' : 
             filter === 'read' ? 'No read messages' : 'No messages yet'}
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            Messages from the contact form will appear here
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredMessages.map((message) => (
            <div
              key={message.id}
              style={{
                background: '#fff',
                border: `2px solid ${message.read ? '#e8e8e8' : '#3498db'}`,
                padding: '24px',
                boxShadow: message.read ? '0 2px 8px rgba(0,0,0,0.06)' : '0 4px 16px rgba(52, 152, 219, 0.15)',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              {!message.read && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '12px',
                  height: '12px',
                  background: '#3498db',
                  borderRadius: '50%'
                }}></div>
              )}
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#222',
                    marginBottom: '4px'
                  }}>
                    {message.name}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '8px'
                  }}>
                    <a 
                      href={`mailto:${message.email}`} 
                      style={{ 
                        color: '#3498db', 
                        textDecoration: 'none',
                        borderBottom: '1px solid transparent',
                        transition: 'border-color 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.borderBottomColor = '#3498db'}
                      onMouseOut={(e) => e.target.style.borderBottomColor = 'transparent'}
                    >
                      {message.email}
                    </a>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#999'
                  }}>
                    {message.timestamp ? new Date(message.timestamp.seconds * 1000).toLocaleString() : 'Unknown date'}
                  </div>
                </div>
                
                <button
                  onClick={() => message.read ? markAsUnread(message.id) : markAsRead(message.id)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    background: message.read ? '#f8f8f8' : '#222',
                    color: message.read ? '#666' : '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {message.read ? 'Mark Unread' : 'Mark Read'}
                </button>
              </div>
              
              <div style={{
                fontSize: '16px',
                color: '#333',
                lineHeight: '1.6',
                background: '#f9f9f9',
                padding: '16px',
                border: '1px solid #eee',
                whiteSpace: 'pre-wrap'
              }}>
                {message.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactMessages;